import DixaIcon from '@dixa/dixa-modern-icons';
import {
  Button,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import _countBy from 'lodash/countBy';
import _filter from 'lodash/filter';
import _find from 'lodash/find';
import _findKey from 'lodash/findKey';
import _forEach from 'lodash/forEach';
import { Component, ReactElement } from 'react';
import {
  Field,
  FieldArray,
  FieldArrayFieldsProps,
  FieldArrayMetaProps,
  Form,
  FormErrors,
  InjectedFormProps,
  reduxForm,
} from 'redux-form';
import validUrl from 'valid-url';

import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import InputField from 'components/shared/util/InputField';
import { getRequestID } from 'components/shared/util/request';
import type {
  ActionNodeT,
  FlowManagerEditModalComponentT,
} from 'fable/flowEditor';

import { URLTriggerModalStatePropsT } from '../../containers/modals/url';
import type { FlowChatNodeURLTriggerT } from '../nodeTypes';

type PropsT = FlowManagerEditModalComponentT<
  FlowChatNodeURLTriggerT | ActionNodeT
> &
  URLTriggerModalStatePropsT;

type InjectedProps = InjectedFormProps<URLTriggerModalFormValuesT, PropsT>;

type FormErrorTypeT = {
  _error: string;
};

type ValidationT = {
  urls?: FormErrorTypeT | Partial<URLT>[];
};

export type URLTriggerModalFormValuesT = {
  urls: URLT[];
  defaultUrl: string;
};

type URLT = {
  value: string;
  label: string;
  isDefault?: boolean;
};

type ListUrlsPropT = {
  fields: FieldArrayFieldsProps<Partial<URLT>>;
  meta: FieldArrayMetaProps & { touched: boolean };
};

const validate = (values: URLTriggerModalFormValuesT): FormErrors => {
  const errors: ValidationT = {};
  //console.warn("validate: ", values);
  if (!values.urls || !values.urls.length) {
    errors.urls = {
      _error: 'At least one URL must be entered - click on "Add URL"',
    };
  } else {
    const urlsArrayErrors: Partial<URLT>[] = [];
    values.urls.forEach(
      (url: { label: string; value: string }, index: number) => {
        const urlErrors: Partial<URLT> = {};
        if (!url || !url.label) {
          urlErrors.label = 'Required';
          urlsArrayErrors[index] = urlErrors;
        } else if (!validUrl.isWebUri(url.label)) {
          urlErrors.label =
            'Please add a valid url - most start with "http://" or "https://"';
          urlsArrayErrors[index] = urlErrors;
        }
      },
    );
    const counts = _countBy(values.urls, (url: URLT) => url.label);
    let index = 0;
    _forEach(counts, (count: number) => {
      if (count > 1) {
        const urlErrors: Partial<URLT> = {};
        urlErrors.label = "You can't have duplicate URLs";
        urlsArrayErrors[index] = urlErrors;
      }
      index++;
    });

    if (urlsArrayErrors.length) {
      errors.urls = urlsArrayErrors;
    }
  }

  return errors;
};

const ListUrls = ({
  fields,
  meta: { error, touched, submitFailed },
}: ListUrlsPropT): ReactElement => {
  return (
    <div>
      <div className="section-header mt10 mb10">
        <h5>URLS</h5>
      </div>
      {(touched || submitFailed) && error && <span>{error}</span>}
      {fields.map((url: string, index: number) => {
        return (
          <div key={index} className="input-field-url">
            <div className="m-b-5">
              <button
                type="button"
                className="btn btn-danger btn-xm remove-url pull-right"
                title="Remove URL"
                onClick={() => fields.remove(index)}
              >
                <DixaIcon icon="close" />
              </button>
              <strong>URL #{index + 1}</strong>
            </div>
            <Field
              name={`${url}.label`}
              isRequired={true}
              type="url"
              isDefaultChecked={`${url}.isDefault`}
              displayName="URL address"
              placeholder={`Eg. http://www.yousite.com`}
              component={InputField}
              className="form-control"
              label="URL"
            />
          </div>
        );
      })}
      <Button
        type="button"
        variant="secondary"
        style={{ width: '100%' }}
        disabled={fields.length >= 12}
        onClick={() => fields.push({})}
      >
        Add URL
      </Button>
    </div>
  );
};

class URLTriggerModal extends Component<PropsT & InjectedProps> {
  @boundMethod
  handleSaveClick(values: URLTriggerModalFormValuesT): void {
    let defaultUrl = '';
    const newURLs = {};
    let nodeState;

    // @FIX - if fixing logic here, ChatMenuModal will probably need the same update

    const oldURLs = this.props.node.payload
      ? this.props.node.payload.urls
      : null;
    const formURLs: URLT[] = values.urls;

    _forEach(formURLs, (formURL: URLT) => {
      newURLs[formURL.label] = formURL.value || getRequestID();
    });

    if (oldURLs) {
      const doesNotExistAnymore = _filter(
        oldURLs,
        (oldNodeID: string) =>
          !_find(
            newURLs,
            (newNodeID: string, newLabel: string) => newNodeID === oldNodeID,
          ),
      );

      _forEach(doesNotExistAnymore, (urlNodeId: string) => {
        const urlLabel =
          _findKey(oldURLs, (id: string) => id === urlNodeId) || '';
        nodeState = this.props.updateConnectionLabel(
          nodeState,
          this.props.node,
          urlLabel,
          '',
        );
      });

      _forEach(oldURLs, (oldNodeId: string, oldLabel: string) => {
        _forEach(newURLs, (newNodeId: string, newLabel: string) => {
          if (newLabel === oldLabel && newNodeId !== oldNodeId) {
            // if ID changed but label is the same, we sever the connection
            // because FlowManager can't reason about which node to connect to
            // or which actionnodes to delete otherwise
            nodeState = this.props.updateConnectionLabel(
              nodeState,
              this.props.node,
              newLabel,
              '',
            );
            newURLs[newLabel] = getRequestID();
          } else if (newLabel !== oldLabel && newNodeId === oldNodeId) {
            // if ID stayed the same but the label changed we update the connection with a new label
            nodeState = this.props.updateConnectionLabel(
              nodeState,
              this.props.node,
              oldLabel,
              newLabel,
            );
          }
        });
      });
    }

    if (
      values.defaultUrl === undefined ||
      values.defaultUrl === null ||
      values.defaultUrl === ''
    ) {
      defaultUrl = getRequestID();
    } else {
      defaultUrl = values.defaultUrl;
    }

    const nodeOptions: FlowChatNodeURLTriggerT = {
      ...this.props.node,
      badges: [],
      icon: '',
      isAction: false,
      isEditable: true,
      isRemovable: true,
      nodeType: FlowNodeTypes.URL,
      payload: {
        urls: newURLs,
        defaultUrl,
      },
      overlay: [],
      text: 'URL',
    };

    this.props.replaceNode(nodeOptions, nodeState);
    this.props.triggerClose();
  }

  @boundMethod
  closeModal() {
    const { destroy, triggerClose } = this.props;
    destroy();
    triggerClose();
  }

  render() {
    const { handleSubmit, node, nodeLabel, dirty: isChanged } = this.props;

    return (
      <div className="url-trigger modal-chatflow-holder">
        <Form onSubmit={handleSubmit(this.handleSaveClick)}>
          <ModalHead title={nodeLabel} onClose={this.closeModal}>
            URL triggers can be used to change how the chat widget looks and
            functions based on which URL the customer is at, when he or she
            starts the conversation.
            <br />
            <br />
            Eg. you can have one queue for your check-out pages and one for
            everything else. <i>https://example.com/support</i> or{' '}
            <i>https://example.com/checkout</i>
          </ModalHead>

          <ModalBody>
            <FieldArray name="urls" component={ListUrls} />
          </ModalBody>

          <ModalFoot
            confirmOnClick={handleSubmit(this.handleSaveClick)}
            confirmName={getConfirmButtonVerb(node)}
            confirmProps={{ disabled: !isChanged }}
            cancelOnClick={this.closeModal}
          />
        </Form>
      </div>
    );
  }
}

export default reduxForm<URLTriggerModalFormValuesT, PropsT>({
  form: 'chatFlowUrlTriggerForm', // A unique identifier for this form.
  enableReinitialize: true,
  validate,
})(URLTriggerModal);
